import { useCallback, useEffect, useMemo, useState } from "react";

import { useOrdersCache } from "@web-app/api";
import {
  Button,
  ConfirmModal,
  ContextMenu,
  type ContextMenuItemProps,
  Header,
  Icon,
  Tabs,
} from "@web-app/components";
import ExpediteDeliveryInfo from "@web-app/pages/common/OrdersSidebar/OrderDetailsPanel/ExpediteDeliveryInfo";
import OnfleetDeliveryInfo from "@web-app/pages/common/OrdersSidebar/OrderDetailsPanel/OnfleetDeliveryInfo";
import RoadieDeliveryInfo from "@web-app/pages/common/OrdersSidebar/OrderDetailsPanel/RoadieDeliveryInfo";
import UberDeliveryInfoV2 from "@web-app/pages/common/OrdersSidebar/OrderDetailsPanel/UberDeliveryInfoV2";
import useDispatchStore from "@web-app/store/dispatchStore";
import { getTimezoneForRoute } from "@web-app/utils/helpers";

import CreateRoute from "./CreateRoute";
import EditRoute from "./EditRoute";
import OrdersTab from "./OrdersTab";
import RouteDetailsTab from "./RouteDetailsTab";
import RouteDriverRoute from "./RouteDriverRoute";
import RouteTimelineTab from "./RouteTimelineTab";
import useManageRoute from "./useManageRoute";

export default function ManageRoute() {
  const {
    route,
    setIsEditing,
    isEditing,
    isCreating,
    setIsCreating,
    deleteRoute,
    isDeleting,
    setIsDeleting,
    showAlertAsync,
    onRouteOrdersCardViewDetails,
  } = useManageRoute();
  const { invalidateOrdersCache } = useOrdersCache();

  const [externalProvider, setExternalProvider] = useState<
    "" | "doordash" | "uber-catering" | "uber"
  >("");

  useEffect(() => {
    let deliveryOption: "" | "doordash" | "uber-catering" | "uber" = "";
    if (route?.details?.externalAssigneeId?.startsWith("uber-catering")) {
      deliveryOption = "uber-catering";
    } else if (route?.details?.externalAssigneeId?.startsWith("uber")) {
      deliveryOption = "uber";
    } else if (route?.details?.externalAssigneeId?.startsWith("doordash")) {
      deliveryOption = "doordash";
    }

    if (route?.details?.externalAssigneeId) {
      setExternalProvider(deliveryOption);
    }
  }, [route?.details?.externalAssigneeId]);

  const handleDeleteRoute = useCallback(() => {
    if (!route?.details?.id) return;

    showAlertAsync(deleteRoute.mutateAsync(route.details.id), {
      error: {
        message: "Failed to delete route.",
        title: "Error",
      },
      loading: {
        message: "Please wait while we delete the route...",
        title: "Deleting Route",
      },
      success: {
        message: "The route has been deleted successfully.",
        title: "Route Deleted",
      },
    });
  }, [deleteRoute, route, showAlertAsync]);

  const menuItems: ContextMenuItemProps[] = useMemo(
    () => [
      {
        disabled: false,
        icon: "fasPencil",
        label: "Edit",
        onClick: () => setIsEditing(true),
      },
      {
        disabled:
          route?.details?.status === "COMPLETE" ||
          !!route?.details?.externalAssignee?.type,
        disabledTooltipMessage: route?.details?.externalAssignee?.type
          ? "Routes assigned to an external provider can't be deleted"
          : "This route is complete and cannot be deleted.",
        icon: "trash",
        label: "Delete",
        onClick: () => setIsDeleting(true),
      },
    ],
    [setIsEditing, setIsDeleting, route],
  );

  if (isEditing) {
    return <EditRoute setIsEditing={setIsEditing} route={route} />;
  }

  if (isCreating) {
    return <CreateRoute setIsCreating={setIsCreating} />;
  }

  return (
    <div className="absolute bg-white h-full w-full z-30 overflow-hidden">
      <Header
        title={route?.details?.externalId || ""}
        borderLess
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={() => {
              useDispatchStore.setState({
                focusedRouteId: null,
                mapFocusedOrderId: null,
                routeModalMode: "closed",
              });
            }}
            plain
            className="!p-0 hover:!bg-white"
          />
        }
        rightActions={<ContextMenu items={menuItems} />}
      />
      <div className="">
        <Tabs
          tabs={[
            {
              content: <RouteDetailsTab route={route} />,
              id: "summary",
              title: (
                <div className="tw-sidebar-tabs-style">
                  <span>Summary</span>
                </div>
              ),
            },
            {
              content: (
                <OrdersTab
                  route={route}
                  onViewDetails={onRouteOrdersCardViewDetails}
                />
              ),
              id: "orders",
              title: (
                <div className="tw-sidebar-tabs-style">
                  <span>Orders</span>
                </div>
              ),
            },
            ...(route?.details
              ? [
                  {
                    content: (
                      <RouteTimelineTab
                        orders={route.orders}
                        route={route.details}
                      />
                    ),
                    id: "timeline",
                    title: (
                      <div className="flex items-center gap-2">
                        <span>Timeline</span>
                      </div>
                    ),
                  },
                ]
              : []),
            ...(route?.details?.assigneeId &&
            route?.details?.status === "COMPLETE"
              ? [
                  {
                    content: (
                      <RouteDriverRoute
                        route={route.details}
                        orders={route.orders}
                      />
                    ),
                    id: "driver-tracking",
                    title: (
                      <div className="tw-sidebar-tabs-style">
                        <span>Driver Tracking</span>
                      </div>
                    ),
                  },
                ]
              : []),
            ...(externalProvider === "uber" ||
            externalProvider === "uber-catering"
              ? [
                  {
                    content: (
                      <UberDeliveryInfoV2
                        externalAssigneeId={
                          route?.details?.externalAssigneeId || ""
                        }
                        entity="route"
                        entityId={route?.details?.id || ""}
                        timezone={getTimezoneForRoute(route?.orders)}
                      />
                    ),
                    id: "uber",
                    title: (
                      <div className="flex items-center gap-2">
                        <span>Uber</span>
                      </div>
                    ),
                  },
                ]
              : []),
            ...(route?.details?.externalAssignee?.type === "roadie"
              ? [
                  {
                    content: (
                      <RoadieDeliveryInfo
                        entity="route"
                        entityId={route?.details?.id || ""}
                        timezone={getTimezoneForRoute(route?.orders)}
                      />
                    ),
                    id: "roadie",
                    title: (
                      <div className="flex items-center gap-2">
                        <span>Roadie</span>
                      </div>
                    ),
                  },
                ]
              : []),
            ...(route?.details?.externalAssignee?.type === "expedite" &&
            route?.details?.externalAssignee?.remark === "accepted"
              ? [
                  {
                    content: (
                      <ExpediteDeliveryInfo
                        entity="route"
                        entityId={route?.details?.id || ""}
                        timezone={getTimezoneForRoute(route?.orders)}
                      />
                    ),
                    id: "expedite",
                    title: (
                      <div className="flex items-center gap-2">
                        <span>Expedite</span>
                      </div>
                    ),
                  },
                ]
              : []),
            ...(route?.details?.externalAssignee?.type === "onfleet"
              ? [
                  {
                    content: (
                      <OnfleetDeliveryInfo
                        entity="route"
                        entityId={route?.details?.id || ""}
                        onCancelSuccess={() => invalidateOrdersCache()}
                      />
                    ),
                    id: "onfleet",
                    title: (
                      <div className="flex items-center gap-2">
                        <span>Onfleet</span>
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </div>
      {isDeleting && (
        <ConfirmModal
          title={
            <div className="flex items-center gap-4">
              <Icon
                name="fasCircleExclamation"
                className="w-6 h-6 text-red-500"
              />
              <span>Delete Route</span>
            </div>
          }
          subtitle="This will delete the route only. The orders inside it will be available in the order panel."
          isOpen
          onClose={() => setIsDeleting(false)}
          actions={[
            {
              color: "red",
              label: "Delete",
              onClick: handleDeleteRoute,
            },
            {
              color: "white",
              label: "Cancel",
              onClick: () => setIsDeleting(false),
            },
          ]}
        />
      )}
    </div>
  );
}
